import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import LegalLinks from '../components/LegalLinks';
// #3187 APPROVE.4 — every change below is behind CLIENT_APPROVAL_ENFORCEMENT_LIVE
// (founder ruling 6564192556): while it is false (or its read fails) this page
// renders and fetches exactly as before, plus the one enforcement read.
import { fetchClientApprovalEnforcementLive } from '../lib/clientApprovalEnforcement';
import type { PendingClientApproval } from '../lib/clientApprovalPortal';
import ClientApprovalsPendingList from '../components/clientApprovals/portal/ClientApprovalsPendingList';
import ClientApprovalHistory from '../components/clientApprovals/portal/ClientApprovalHistory';
import ClientApprovalLeadPanel from '../components/clientApprovals/portal/ClientApprovalLeadPanel';
import EndPartnershipRequest from '../components/clientApprovals/portal/EndPartnershipRequest';

// ── Types ────────────────────────────────────────────────────────────────────

interface MyProject {
  projectId: string;
  projectName: string;
  domain: string | null;
  stakeholderId: string;
}

interface ProjectSummary {
  projectName: string;
  domain: string | null;
  currentPhase: string;
  phaseNumber: number;
  sprintNumber: number;
  sprintDay: number;
  sprintTotal: number;
  storiesDone: number;
  storiesInProgress: number;
  storiesBlocked: number;
  healthStatus: 'GREEN' | 'AMBER' | 'RED';
  liveUrl: string | null;
  pendingCOCount: number;
  pendingSignOffCount: number;
}

interface PendingAcknowledgement {
  id: string;
  decisionId: string;
  projectId: string;
  status: string;
  expiresAt: string;
  decisionTitle: string;
  businessImpactReason: string | null;
  hoursRemaining: number;
}

interface ClientApprovalPending {
  artefactId: string;
  artefactTitle: string;
  artefactType: string;
  projectName: string;
  submittedAt: string;
  daysPending: number;
}

interface DeliveryEvent {
  type: string;
  description: string;
  occurredAt: string;
  artefactId: string | null;
}

interface SignoffInfo {
  signoffs: Array<{
    id: string;
    stakeholderId: string;
    status: string;
    comment: string | null;
    stakeholderName: string;
  }>;
  total: number;
  approved: number;
  changesRequested: number;
  pending: number;
}

// ── Health badge colors ──────────────────────────────────────────────────────

const HEALTH_COLORS: Record<string, { bg: string; text: string }> = {
  GREEN: { bg: 'rgba(34,197,94,0.1)', text: '#16a34a' },
  AMBER: { bg: 'rgba(245,158,11,0.1)', text: '#d97706' },
  RED:   { bg: 'rgba(239,68,68,0.1)',  text: '#dc2626' },
};

const PHASE_LABELS: Record<string, string> = {
  phase_0_bootstrap:        'Bootstrap',
  phase_1_requirements:     'Requirements',
  phase_2_architecture:     'Architecture',
  phase_2_5_ux_design:      'UX Design',
  phase_3_sprint_planning:  'Sprint Planning',
  phase_4_development:      'Development',
  phase_5_code_review:      'Code Review',
  phase_6_testing:          'Testing',
  phase_7_deployment:       'Deployment',
  phase_8_retrospective:    'Retrospective',
};

// Sprint 11 UX.A1: regex /phase_(\d+)/ truncates phase_2_5_ux_design to "2".
// Use an explicit map so the awaiting-approval card reads "Phase 2.5" not "Phase 2".
const PHASE_DISPLAY_NUMBER: Record<string, string> = {
  phase_0_bootstrap:        '0',
  phase_1_requirements:     '1',
  phase_2_architecture:     '2',
  phase_2_5_ux_design:      '2.5',
  phase_3_sprint_planning:  '3',
  phase_4_development:      '4',
  phase_5_code_review:      '5',
  phase_6_testing:          '6',
  phase_7_deployment:       '7',
  phase_8_retrospective:    '8',
};

// ── Main Component ───────────────────────────────────────────────────────────

export default function ClientDashboard() {
  const navigate = useNavigate();
  // #2220 — carried from ClientProjectPicker (or a direct link) for a client
  // with stakeholder access on more than one project. Read once at mount;
  // switching projects always routes back through the picker (a different
  // route, so a fresh mount), not by changing this query param in place —
  // no need to add it to loadData's effect deps below.
  const [searchParams] = useSearchParams();
  const requestedProjectId = searchParams.get('projectId');
  const [myProject, setMyProject] = useState<MyProject | null>(null);
  const [summary, setSummary] = useState<ProjectSummary | null>(null);
  const [history, setHistory] = useState<DeliveryEvent[]>([]);
  const [signoffPhases, setSignoffPhases] = useState<Array<{ phaseId: string; info: SignoffInfo }>>([]);
  // #3229 A1 (Codex R2-05) — only a phase with a PENDING request has anything
  // to act on. One whose requests were all WITHDRAWN (the stakeholder was
  // removed) stays awaiting_signoff, but neither gets a card nor hides the
  // empty state.
  const actionableSignoffPhases = signoffPhases.filter(({ info }) => info.signoffs.some(s => s.status === 'PENDING'));
  const [signoffComment, setSignoffComment] = useState('');
  const [signoffApproved, setSignoffApproved] = useState(true);
  const [submittingSignoff, setSubmittingSignoff] = useState(false);
  const [pendingAcks, setPendingAcks] = useState<PendingAcknowledgement[]>([]);
  const [ackNotes, setAckNotes] = useState<Record<string, string>>({});
  const [submittingAck, setSubmittingAck] = useState<string | null>(null);
  const [ackSuccess, setAckSuccess] = useState<string | null>(null);
  const [pendingApprovals, setPendingApprovals] = useState<ClientApprovalPending[]>([]);
  const [approvalMode, setApprovalMode] = useState<Record<string, 'APPROVE' | 'REQUEST_CHANGES'>>({});
  const [approvalComments, setApprovalComments] = useState<Record<string, string>>({});
  const [submittingApproval, setSubmittingApproval] = useState<Record<string, boolean>>({});
  const [approvalSuccess, setApprovalSuccess] = useState<Record<string, boolean>>({});
  const [approvalErrors, setApprovalErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // #3187 APPROVE.4 — the enforcement constant, and 3a's pending list when it is on.
  const [enforcementLive, setEnforcementLive] = useState(false);
  const [clientApprovalsPending, setClientApprovalsPending] = useState<PendingClientApproval[] | null>([]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [proj, live] = await Promise.all([
        apiFetch<MyProject>(
          requestedProjectId
            ? `/client/my-project?projectId=${encodeURIComponent(requestedProjectId)}`
            : '/client/my-project',
        ),
        fetchClientApprovalEnforcementLive(),
      ]);
      setMyProject(proj);
      setEnforcementLive(live);

      // #3187 APPROVE.4 (live only): 3a's pending list replaces the legacy
      // list, and the phase sign-off card goes (design v1.1 §5.4), so neither
      // the legacy list nor /phase-states is read (#3187 acceptance).
      if (live) {
        const [summ, hist, acks, pending] = await Promise.all([
          apiFetch<ProjectSummary>(`/projects/${proj.projectId}/client/summary`),
          apiFetch<DeliveryEvent[]>(`/projects/${proj.projectId}/client/delivery-history`),
          apiFetch<PendingAcknowledgement[]>(`/projects/${proj.projectId}/decisions/pending-acknowledgements`).catch(() => [] as PendingAcknowledgement[]),
          // R1 P2-4: a failed read is null (shown as a failure), never an empty list.
          apiFetch<PendingClientApproval[]>(`/client-approval-requests/pending?projectId=${encodeURIComponent(proj.projectId)}`).catch(() => null),
        ]);
        setSummary(summ);
        setHistory(hist);
        setPendingAcks(acks);
        setClientApprovalsPending(Array.isArray(pending) ? pending : null);
        setPendingApprovals([]);
        setSignoffPhases([]);
        return;
      }

      const [summ, hist, acks, approvals] = await Promise.all([
        apiFetch<ProjectSummary>(`/projects/${proj.projectId}/client/summary`),
        apiFetch<DeliveryEvent[]>(`/projects/${proj.projectId}/client/delivery-history`),
        apiFetch<PendingAcknowledgement[]>(`/projects/${proj.projectId}/decisions/pending-acknowledgements`).catch(() => [] as PendingAcknowledgement[]),
        apiFetch<ClientApprovalPending[]>(`/projects/${proj.projectId}/client-approvals/pending`).catch(() => [] as ClientApprovalPending[]),
      ]);
      setSummary(summ);
      setHistory(hist);
      setPendingAcks(acks);
      setPendingApprovals(approvals);

      // Drop per-item transient state (comment, mode, error, submitting/success
      // flags) for any artefactId no longer in the fresh pending list. Without
      // this, an artefactId that leaves the list after approval and later
      // reappears — the team fixes and resubmits under the same artefactId,
      // re-arming client review — would render as still-approved from stale
      // `approvalSuccess` state, hiding the new pending request entirely.
      const stillPendingIds = new Set(approvals.map(a => a.artefactId));
      const pruneToStillPending = <T,>(record: Record<string, T>): Record<string, T> => {
        const next: Record<string, T> = {};
        for (const [id, value] of Object.entries(record)) {
          if (stillPendingIds.has(id)) next[id] = value;
        }
        return next;
      };
      setApprovalMode(prev => pruneToStillPending(prev));
      setApprovalComments(prev => pruneToStillPending(prev));
      setApprovalErrors(prev => pruneToStillPending(prev));
      setApprovalSuccess(prev => pruneToStillPending(prev));
      setSubmittingApproval(prev => pruneToStillPending(prev));

      // Load signoff status for phases in awaiting_signoff state
      if (summ.pendingSignOffCount > 0) {
        try {
          // Load phase states to find which phases need signoff
          const phaseStates = await apiFetch<Array<{ phase: string; status: string }>>(
            `/projects/${proj.projectId}/phase-states`,
          );
          const awaitingPhases = (phaseStates as any)?.phases
            ? (phaseStates as any).phases.filter((p: any) => p.status === 'awaiting_signoff')
            : [];

          const signoffs = await Promise.all(
            awaitingPhases.map(async (p: any) => {
              const info = await apiFetch<SignoffInfo>(
                `/projects/${proj.projectId}/phases/${p.phase}/signoff-status`,
              );
              return { phaseId: p.phase as string, info };
            }),
          );
          setSignoffPhases(signoffs);
        } catch { /* signoff info is optional */ }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('404')) {
        navigate('/client/onboarding');
        return;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (error) return <div style={{ padding: 40, color: '#ef4444' }}>{error}</div>;
  if (!summary || !myProject) return null;

  const phaseLabel = PHASE_LABELS[summary.currentPhase] ?? summary.currentPhase;
  const phasePct = Math.round((summary.phaseNumber / 7) * 100);
  const healthColor = HEALTH_COLORS[summary.healthStatus] ?? HEALTH_COLORS.GREEN;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: 32 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, fontFamily: 'var(--font-heading)' }}>
          Client Dashboard
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* DEF-13 — the open-questions agenda (/projects/:projectId/open-
              questions) had no reachable link from this dashboard at all;
              CLIENT_SPONSOR reviewers are already admitted server-side
              (assertArtefactProjectOrTenantAccess, Piece 2), they just had
              no way to get there. */}
          <button
            type="button"
            data-testid="open-questions-link"
            onClick={() => navigate(`/projects/${myProject.projectId}/open-questions`)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 13, color: 'var(--primary)', fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            Open Questions
          </button>
          {/* #2220 — round-trips through the picker even for a single-project
              client (it auto-redirects back here); avoids a second fetch just
              to decide whether to show this link. */}
          <button
            type="button"
            data-testid="switch-project-link"
            onClick={() => navigate('/client/select-project')}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 13, color: 'var(--primary)', fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            Switch project
          </button>
        </div>
      </div>
      <p style={{ color: 'var(--text-secondary, #6b7280)', marginBottom: 32, fontSize: 14 }}>
        Welcome back. Here&apos;s the latest on your project.
      </p>

      {/* ── SECTION 1: Awaiting Approval ──────────────────────────────── */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Awaiting Your Approval</h2>

        {/* #3187 APPROVE.4 (live only) — "Needs your approval", from 3a's list. */}
        {enforcementLive && (clientApprovalsPending === null ? (
          <div data-testid="client-approvals-load-failed" role="alert" style={{ padding: 16, borderRadius: 8, border: '1px solid var(--border)', color: '#dc2626', fontSize: 14, marginBottom: 12 }}>
            We couldn&apos;t load your approvals. Reload the page to try again.
          </div>
        ) : <ClientApprovalsPendingList items={clientApprovalsPending} />)}

        {/* Phase sign-off cards. #3187 APPROVE.4: gone only when
            CLIENT_APPROVAL_ENFORCEMENT_LIVE is true, where loadData never
            reads /phase-states, so there is nothing to show (design v1.1 §5.4). */}
        {actionableSignoffPhases.map(({ phaseId, info }) => {
          const phaseNum = PHASE_DISPLAY_NUMBER[phaseId] ?? '?';
          const phaseLabel = PHASE_LABELS[phaseId] ?? phaseId;
          const mySignoff = info.signoffs.find(s => s.status === 'PENDING');

          if (!mySignoff) return null;

          return (
            <div
              key={phaseId}
              data-testid="signoff-card"
              style={{
                padding: 16,
                borderRadius: 8,
                border: '1px solid var(--border)',
                borderLeft: '4px solid #7c3aed',
                backgroundColor: 'var(--surface)',
                marginBottom: 12,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 600, color: '#7c3aed', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Phase Complete — Your sign-off needed
              </div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>
                Phase {phaseNum} — {phaseLabel} is complete
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name={`signoff-${phaseId}`}
                    checked={signoffApproved}
                    onChange={() => setSignoffApproved(true)}
                  />
                  Approve &amp; proceed
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name={`signoff-${phaseId}`}
                    checked={!signoffApproved}
                    onChange={() => setSignoffApproved(false)}
                  />
                  Request changes
                </label>
              </div>

              <textarea
                data-testid="signoff-comment"
                placeholder="Comment (optional)"
                value={signoffComment}
                onChange={e => setSignoffComment(e.target.value)}
                style={{
                  width: '100%', padding: 8, borderRadius: 6,
                  border: '1px solid var(--border)',
                  fontSize: 13, resize: 'vertical', minHeight: 60,
                  marginBottom: 12,
                }}
              />

              <button
                data-testid="signoff-submit"
                disabled={submittingSignoff}
                onClick={async () => {
                  setSubmittingSignoff(true);
                  try {
                    await apiFetch(`/projects/${myProject!.projectId}/phases/${phaseId}/gate/client-signoff`, {
                      method: 'POST',
                      body: JSON.stringify({ approved: signoffApproved, comment: signoffComment || undefined }),
                    });
                    loadData();
                  } catch { /* handle error */ }
                  setSubmittingSignoff(false);
                }}
                style={{
                  padding: '8px 20px', borderRadius: 6,
                  backgroundColor: signoffApproved ? '#7c3aed' : '#f59e0b',
                  color: '#fff', border: 'none', cursor: 'pointer',
                  fontWeight: 500, fontSize: 13,
                }}
              >
                {submittingSignoff ? 'Submitting...' : 'Submit →'}
              </button>
            </div>
          );
        })}

        {/* PRD approval cards (#2151) */}
        {pendingApprovals.map((item) => {
          const mode = approvalMode[item.artefactId] ?? 'APPROVE';
          const comment = approvalComments[item.artefactId] ?? '';
          const submitting = submittingApproval[item.artefactId] === true;
          const succeeded = approvalSuccess[item.artefactId] === true;
          const error = approvalErrors[item.artefactId];

          return (
            <div
              key={item.artefactId}
              data-testid="approval-card"
              style={{
                padding: 16,
                borderRadius: 8,
                border: '1px solid var(--border)',
                borderLeft: '4px solid #0d9488',
                backgroundColor: succeeded ? 'rgba(34,197,94,0.05)' : 'var(--surface)',
                marginBottom: 12,
              }}
            >
              {succeeded ? (
                <div style={{ color: '#16a34a', fontWeight: 500, fontSize: 14 }}>
                  {mode === 'REQUEST_CHANGES' ? 'Changes requested. The team has been notified.' : 'Approved. Recorded in audit trail.'}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#0d9488', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    PRD Awaiting Your Review
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{item.artefactTitle}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginBottom: 12 }}>
                    {item.artefactType} · {item.daysPending} day{item.daysPending !== 1 ? 's' : ''} pending
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name={`approval-${item.artefactId}`}
                        checked={mode === 'APPROVE'}
                        onChange={() => {
                          setApprovalMode(prev => ({ ...prev, [item.artefactId]: 'APPROVE' }));
                          // A comment typed while Request Changes was selected is not
                          // sent with an approval (the endpoint never reads `reason`
                          // for APPROVE) — clear it so the box doesn't silently hold
                          // text the submit is about to discard.
                          setApprovalComments(prev => {
                            const next = { ...prev };
                            delete next[item.artefactId];
                            return next;
                          });
                          setApprovalErrors(prev => {
                            const next = { ...prev };
                            delete next[item.artefactId];
                            return next;
                          });
                        }}
                      />
                      Approve
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name={`approval-${item.artefactId}`}
                        checked={mode === 'REQUEST_CHANGES'}
                        onChange={() => {
                          setApprovalMode(prev => ({ ...prev, [item.artefactId]: 'REQUEST_CHANGES' }));
                          setApprovalErrors(prev => {
                            const next = { ...prev };
                            delete next[item.artefactId];
                            return next;
                          });
                        }}
                      />
                      Request Changes
                    </label>
                  </div>

                  <textarea
                    data-testid="approval-comment"
                    placeholder="What needs to change? (minimum 10 characters)"
                    value={comment}
                    onChange={e => setApprovalComments(prev => ({ ...prev, [item.artefactId]: e.target.value }))}
                    style={{
                      width: '100%', padding: 8, borderRadius: 6,
                      border: '1px solid var(--border)',
                      fontSize: 13, resize: 'vertical', minHeight: 60,
                      marginBottom: 12,
                    }}
                  />

                  {error && (
                    <div data-testid="approval-error" style={{ color: '#dc2626', fontSize: 12, marginBottom: 12 }}>
                      {error}
                    </div>
                  )}

                  <button
                    data-testid="approval-submit"
                    disabled={submitting}
                    onClick={async () => {
                      const artefactId = item.artefactId;
                      const trimmed = comment.trim();

                      if (mode === 'REQUEST_CHANGES' && trimmed.length < 10) {
                        setApprovalErrors(prev => ({ ...prev, [artefactId]: 'Reason required (minimum 10 characters).' }));
                        return;
                      }

                      setApprovalErrors(prev => {
                        const next = { ...prev };
                        delete next[artefactId];
                        return next;
                      });
                      setSubmittingApproval(prev => ({ ...prev, [artefactId]: true }));
                      try {
                        await apiFetch(`/artefacts/${artefactId}/client-approval`, {
                          method: 'POST',
                          body: JSON.stringify(
                            mode === 'REQUEST_CHANGES'
                              ? { action: 'REQUEST_CHANGES', reason: trimmed }
                              : { action: 'APPROVE' },
                          ),
                        });
                        setApprovalSuccess(prev => ({ ...prev, [artefactId]: true }));
                        setTimeout(() => loadData(), 2000);
                      } catch (err: unknown) {
                        const message = err instanceof ApiError && err.status === 400
                          ? err.message
                          : 'Something went wrong. Please try again or contact your BluePrint administrator.';
                        setApprovalErrors(prev => ({ ...prev, [artefactId]: message }));
                      }
                      // Keyed update, not a blanket reset — must not clear a
                      // DIFFERENT card's in-flight submitting flag (#2151 R1/R2
                      // review: this was a shared string | null before, and an
                      // unconditional reset here cleared whichever card started
                      // submitting most recently, re-enabling every other card's
                      // button mid-request).
                      setSubmittingApproval(prev => ({ ...prev, [artefactId]: false }));
                    }}
                    style={{
                      padding: '8px 20px', borderRadius: 6,
                      backgroundColor: mode === 'APPROVE' ? '#7c3aed' : '#f59e0b',
                      color: '#fff', border: 'none', cursor: 'pointer',
                      fontWeight: 500, fontSize: 13,
                    }}
                  >
                    {submitting ? 'Submitting...' : mode === 'APPROVE' ? 'Approve' : 'Request Changes'}
                  </button>
                </>
              )}
            </div>
          );
        })}

        {/* ADR Acknowledgement cards (ROLES.C2) */}
        {pendingAcks.map((ack) => (
          <div
            key={ack.id}
            data-testid="ack-card"
            style={{
              padding: 16,
              borderRadius: 8,
              border: '1px solid var(--border)',
              borderLeft: '4px solid #2563eb',
              backgroundColor: ackSuccess === ack.id ? 'rgba(34,197,94,0.05)' : 'var(--surface)',
              marginBottom: 12,
            }}
          >
            {ackSuccess === ack.id ? (
              <div style={{ color: '#16a34a', fontWeight: 500, fontSize: 14 }}>
                Acknowledged. Recorded in audit trail.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#2563eb', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Architecture Decision — Acknowledgement requested
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{ack.decisionTitle}</div>
                {ack.businessImpactReason && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginBottom: 8 }}>
                    Business impact: {ack.businessImpactReason}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#d97706', marginBottom: 12 }}>
                  Acknowledges automatically in {ack.hoursRemaining}h unless you respond
                </div>

                <textarea
                  data-testid="ack-notes"
                  placeholder="Add notes (optional)"
                  value={ackNotes[ack.decisionId] ?? ''}
                  onChange={e => setAckNotes(prev => ({ ...prev, [ack.decisionId]: e.target.value }))}
                  style={{
                    width: '100%', padding: 8, borderRadius: 6,
                    border: '1px solid var(--border)',
                    fontSize: 13, resize: 'vertical', minHeight: 48,
                    marginBottom: 12,
                  }}
                />

                <button
                  data-testid="ack-submit"
                  disabled={submittingAck === ack.id}
                  onClick={async () => {
                    setSubmittingAck(ack.id);
                    try {
                      await apiFetch(`/projects/${ack.projectId}/decisions/${ack.decisionId}/acknowledge`, {
                        method: 'POST',
                        body: JSON.stringify({ notes: ackNotes[ack.decisionId] || undefined }),
                      });
                      setAckSuccess(ack.id);
                      setTimeout(() => loadData(), 2000);
                    } catch { /* handle error */ }
                    setSubmittingAck(null);
                  }}
                  style={{
                    padding: '8px 20px', borderRadius: 6,
                    backgroundColor: '#2563eb',
                    color: '#fff', border: 'none', cursor: 'pointer',
                    fontWeight: 500, fontSize: 13,
                  }}
                >
                  {submittingAck === ack.id ? 'Acknowledging...' : 'Acknowledge'}
                </button>
              </>
            )}
          </div>
        ))}

        {/* #3187 APPROVE.4: the empty state, recomputed from the new list when live
            (an answered request waiting on others needs nothing from this viewer). */}
        {(enforcementLive
          ? summary.pendingCOCount === 0 && pendingAcks.length === 0 && clientApprovalsPending !== null && !clientApprovalsPending.some((p) => p.canRespond)
          : summary.pendingCOCount === 0 && actionableSignoffPhases.length === 0 && pendingAcks.length === 0 && pendingApprovals.length === 0) ? (
          <div
            data-testid="no-pending-approvals"
            style={{
              padding: 20,
              borderRadius: 8,
              backgroundColor: 'var(--surface-alt)',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary, #6b7280)',
              fontSize: 14,
            }}
          >
            Nothing needs your attention right now.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Placeholder for COs — real data would come from a dedicated endpoint.
                Gated on its own count (pre-existing bug, not #2151's: this whole
                branch used to render whenever ANY of pendingCOCount/signoffPhases/
                pendingAcks was non-empty, showing a "0 pending change orders" card
                alongside e.g. a genuinely pending PRD approval — #2151 R2 review
                flagged this as newly, commonly reachable once PRD-approval-only
                became a real state this branch has to handle). */}
            {summary.pendingCOCount > 0 && (
              <div
                data-testid="pending-co-card"
                style={{
                  padding: 16,
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  borderLeft: '4px solid #f59e0b',
                  backgroundColor: 'var(--surface)',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: '#d97706', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Change Order — Requires your approval
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                  {summary.pendingCOCount} pending change order{summary.pendingCOCount !== 1 ? 's' : ''}
                </div>
                <button
                  onClick={() => navigate(`/projects/${myProject.projectId}/amendments`)}
                  style={{
                    padding: '6px 16px',
                    borderRadius: 6,
                    border: '1px solid var(--border)',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                >
                  Review Change Orders &rarr;
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* #3187 APPROVE.4 (live only) — approval history, the Lead Approver
          panel and MANAGED confirm (each renders nothing for a viewer the API
          refuses), and the MANAGED contact's request to end the partnership. */}
      {enforcementLive && (
        <>
          <ClientApprovalLeadPanel projectId={myProject.projectId} />
          <ClientApprovalHistory projectId={myProject.projectId} />
          <EndPartnershipRequest projectId={myProject.projectId} />
        </>
      )}

      {/* ── SECTION 2: Project Progress ───────────────────────────────── */}
      <section style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Project Progress</h2>
        <div
          data-testid="project-progress"
          style={{
            padding: 20,
            borderRadius: 8,
            border: '1px solid var(--border)',
            backgroundColor: 'var(--surface)',
          }}
        >
          {/* Project name + domain */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 20, fontWeight: 700 }}>{summary.projectName}</span>
            {summary.domain && (
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 9999,
                backgroundColor: 'rgba(124,58,237,0.1)', color: '#7c3aed', fontWeight: 500,
              }}>
                {summary.domain}
              </span>
            )}
          </div>

          {/* Phase progress bar */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-secondary, #6b7280)', marginBottom: 4 }}>
              <span>Phase {summary.phaseNumber} — {phaseLabel}</span>
              <span>{phasePct}%</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, backgroundColor: 'var(--border)' }}>
              <div style={{
                height: '100%', borderRadius: 3, width: `${phasePct}%`,
                backgroundColor: '#7c3aed', transition: 'width 300ms',
              }} />
            </div>
          </div>

          {/* Sprint info */}
          <div style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)', marginBottom: 12 }}>
            Sprint {summary.sprintNumber} · Day {summary.sprintDay} of {summary.sprintTotal}
          </div>

          {/* Story counts */}
          <div style={{ display: 'flex', gap: 16, fontSize: 13, marginBottom: 12 }}>
            <span><strong data-testid="stories-done">{summary.storiesDone}</strong> done</span>
            <span><strong>{summary.storiesInProgress}</strong> in progress</span>
            {summary.storiesBlocked > 0 && (
              <span style={{ color: '#ef4444' }}><strong>{summary.storiesBlocked}</strong> blocked</span>
            )}
          </div>

          {/* Health badge */}
          <span
            data-testid={`health-badge-${summary.healthStatus.toLowerCase()}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 9999,
              backgroundColor: healthColor.bg, color: healthColor.text,
              fontSize: 12, fontWeight: 600,
            }}
          >
            <span style={{
              width: 7, height: 7, borderRadius: '50%',
              backgroundColor: healthColor.text,
            }} />
            {summary.healthStatus}
          </span>

          {/* Live URL */}
          {summary.liveUrl && (
            <div style={{ marginTop: 12 }}>
              <a href={summary.liveUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 13, color: '#7c3aed' }}>
                View Live Site &rarr;
              </a>
            </div>
          )}
        </div>
      </section>

      {/* ── SECTION 3: Delivery History ───────────────────────────────── */}
      <section>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Delivery History</h2>
        {history.length === 0 ? (
          <p style={{ color: 'var(--text-secondary, #6b7280)', fontSize: 14 }}>
            No delivery events yet.
          </p>
        ) : (
          <div data-testid="delivery-history" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {history.map((event, i) => (
              <div
                key={i}
                style={{
                  display: 'flex', gap: 12, padding: '12px 0',
                  borderBottom: i < history.length - 1 ? '1px solid var(--border)' : 'none',
                }}
              >
                <span style={{ fontSize: 16, flexShrink: 0 }}>
                  {event.type.includes('approved') || event.type.includes('completed') ? '✓' : '⚡'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{event.description}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary, #6b7280)', marginTop: 2 }}>
                    {formatTimeAgo(event.occurredAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* #2504 (RZP.C1) — Terms/Privacy. Rendered here, not only in Settings:
          a client stakeholder has no tenantId claim, so ProtectedRoute
          redirects them away from /settings entirely. This is their only
          reachable surface for the links. */}
      <LegalLinks style={{ marginTop: 32 }} />
    </div>
  );
}

function formatTimeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
